/**
 * DomainKnowledgeDashboard
 *
 * Main page for managing domain knowledge - viewing, creating, researching,
 * approving, and tracking stale content.
 *
 * Part of Story 30: Domain Knowledge Refinement System
 *
 * Date: December 18, 2025
 */

import React, { useState, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen,
  AlertTriangle,
  FlaskConical,
  Plus,
  RefreshCw,
  Loader2,
  Play,
  Clock,
  CheckCircle2,
  XCircle,
  Sparkles,
} from 'lucide-react';
import { format } from 'date-fns';
import { useSite } from '@/contexts/SiteContext';
import {
  KnowledgeList,
  KnowledgeDetails,
  KnowledgeForm,
  StalenessQueue,
  DomainKnowledgeGuidance,
} from '@/components/domain-knowledge';
import { EnrichmentOpportunitiesTab } from '@/components/enrichment';
import { DomainKnowledge } from '@/services/domainKnowledgeService';
import { getSummary, OpportunitySummary } from '@/services/enrichmentOpportunityService';

export default function DomainKnowledgeDashboard() {
  const { selectedSite } = useSite();
  const [activeTab, setActiveTab] = useState('knowledge');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Knowledge details panel
  const [selectedKnowledge, setSelectedKnowledge] = useState<DomainKnowledge | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Knowledge form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editingKnowledge, setEditingKnowledge] = useState<DomainKnowledge | null>(null);

  const handleRefresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleViewKnowledge = useCallback((knowledge: DomainKnowledge) => {
    setSelectedKnowledge(knowledge);
    setDetailsOpen(true);
  }, []);

  const handleEditKnowledge = useCallback((knowledge: DomainKnowledge) => {
    setEditingKnowledge(knowledge);
    setFormOpen(true);
  }, []);

  const handleCreateKnowledge = useCallback(() => {
    setEditingKnowledge(null);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingKnowledge(null);
  }, []);

  const handleDetailsClose = useCallback(() => {
    setDetailsOpen(false);
    setSelectedKnowledge(null);
  }, []);

  const handleActionComplete = useCallback(() => {
    handleRefresh();
    handleDetailsClose();
  }, [handleRefresh, handleDetailsClose]);

  const handleFormSaved = useCallback(() => {
    handleRefresh();
  }, [handleRefresh]);

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <BookOpen className="h-8 w-8" />
            Domain Knowledge
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage knowledge rules for content generation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleCreateKnowledge}>
            <Plus className="h-4 w-4 mr-2" />
            Add Knowledge
          </Button>
        </div>
      </div>

      {/* Site Notice */}
      {!selectedSite && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
          Please select a site from the header to view site-specific knowledge.
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="knowledge" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Knowledge
          </TabsTrigger>
          <TabsTrigger value="opportunities" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Opportunities
          </TabsTrigger>
          <TabsTrigger value="staleness" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Stale Content
          </TabsTrigger>
          <TabsTrigger value="research" className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4" />
            Research
          </TabsTrigger>
        </TabsList>

        {/* Contextual Guidance */}
        <div className="mt-6">
          <DomainKnowledgeGuidance activeTab={activeTab} />
        </div>

        {/* Knowledge Tab */}
        <TabsContent value="knowledge" className="mt-6">
          <KnowledgeList
            onView={handleViewKnowledge}
            onEdit={handleEditKnowledge}
            onCreate={handleCreateKnowledge}
            refreshTrigger={refreshTrigger}
          />
        </TabsContent>

        {/* Enrichment Opportunities Tab */}
        <TabsContent value="opportunities" className="mt-6">
          <EnrichmentOpportunitiesTab />
        </TabsContent>

        {/* Staleness Tab */}
        <TabsContent value="staleness" className="mt-6">
          <StalenessQueue refreshTrigger={refreshTrigger} />
        </TabsContent>

        {/* Research Tab */}
        <TabsContent value="research" className="mt-6">
          <ResearchQueueView refreshTrigger={refreshTrigger} onRefresh={handleRefresh} />
        </TabsContent>
      </Tabs>

      {/* Knowledge Details Panel */}
      <KnowledgeDetails
        open={detailsOpen}
        onClose={handleDetailsClose}
        knowledge={selectedKnowledge}
        onActionComplete={handleActionComplete}
      />

      {/* Knowledge Form Dialog */}
      <KnowledgeForm
        open={formOpen}
        onClose={handleFormClose}
        knowledge={editingKnowledge}
        onSaved={handleFormSaved}
      />
    </div>
  );
}

/**
 * ResearchQueueView - Inline component for research tab
 */
function ResearchQueueView({
  refreshTrigger,
  onRefresh,
}: {
  refreshTrigger: number;
  onRefresh: () => void;
}) {
  const { selectedSite } = useSite();
  const [queue, setQueue] = useState<any[]>([]);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  React.useEffect(() => {
    loadData();
  }, [selectedSite?.id, refreshTrigger]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { getResearchQueue, getResearchStats } = await import(
        '@/services/domainKnowledgeService'
      );

      const [queueRes, statsRes] = await Promise.all([
        getResearchQueue(selectedSite?.id),
        getResearchStats(selectedSite?.id),
      ]);

      if (queueRes.success && queueRes.data) {
        setQueue(queueRes.data);
      }
      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (err) {
      console.error('[ResearchQueueView] Error loading:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessNext = async () => {
    setProcessing(true);
    try {
      const response = await fetch('/api/domain-knowledge/research/process-next', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId: selectedSite?.id }),
      });
      const data = await response.json();
      if (data.success) {
        onRefresh();
      }
    } catch (err) {
      console.error('[ResearchQueueView] Error processing:', err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{stats.pending || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold">{stats.in_progress || 0}</p>
              </div>
              <FlaskConical className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">{stats.completed || 0}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Failed</p>
                <p className="text-2xl font-bold">{stats.failed || 0}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Queue */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5" />
              Research Queue
              <Badge variant="secondary">{queue.length}</Badge>
            </CardTitle>
            <Button
              size="sm"
              onClick={handleProcessNext}
              disabled={processing || queue.length === 0}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-1" />
              )}
              Process Next
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : queue.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No research items in queue
            </div>
          ) : (
            <div className="space-y-3">
              {queue.map((item: any) => (
                <div
                  key={item.id}
                  className="p-3 border rounded-lg flex items-start justify-between"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {item.research_question}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <Badge variant="outline">{item.knowledge_type}</Badge>
                      <Badge variant="outline">{item.scope_level}</Badge>
                      <span>{format(new Date(item.created_at), 'MMM d, HH:mm')}</span>
                    </div>
                  </div>
                  <Badge>{item.research_methodology}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
